import { ratesByState } from '@/app/(marketplace)/quote-builder/electricity-rates/rates';

import { PROJECTION_YEARS } from '../data/content';
import { knownRateChanges, KnownRateChange } from '../data/knownRateChanges';
import { ProjectionScenario, stateEscalator, steoForecast } from '../data/rateProjections';

import { scenarioEscalator } from './scenarios';
import { BillPoint, ProjectionInput } from './types';

type MonthlyBillInput = Omit<ProjectionInput, 'homeSqft'>;

/** The entered bill anchors to the CURRENT year — one past the latest full EIA year */
export const todayYear = (stateCode: string): number | undefined => {
  const rates = ratesByState[stateCode]?.rates;
  return rates?.length ? rates[rates.length - 1].year + 1 : undefined;
};

// This year's rate step: an announced filing if on record, else EIA's
// short-term forecast, else the state's long-run AEO path. The last fallback is
// deliberately independent of the reader's Forecast Type — this step also
// re-prices the history bars, which must not move when the picker does.
export const todayStepFraction = (
  stateCode: string,
  knownChanges: Record<string, KnownRateChange[]> = knownRateChanges,
  steoByYear: Record<number, number> = steoForecast
): { fraction: number; known?: KnownRateChange; steo: boolean } => {
  const year = todayYear(stateCode);
  const known = year === undefined ? undefined : knownChanges[stateCode]?.find((change) => change.year === year);
  const steo = year === undefined ? undefined : steoByYear[year];
  return {
    fraction: known?.impact ?? steo ?? stateEscalator(stateCode),
    known,
    steo: steo !== undefined,
  };
};

// Monthly-bill series for the hero chart: the entered bill is TODAY (current
// year — an August bill already reflects this year's rates), back-cast over
// the actual EIA rate history (constant usage assumed), then projected forward
// on a layered path — announced rate cases override everything for their year;
// EIA STEO's national forecast owns the years it covers; every later year
// compounds at the reader-selected scenario's escalator (engine/scenarios.ts;
// memo in docs/plans/bill-projection-escalator-model.md). Injectable tables
// keep tests deterministic.
export const projectMonthlyBills = (
  { monthlyBillUsd, monthlyKwh, stateCode }: MonthlyBillInput,
  knownChanges: Record<string, KnownRateChange[]> = knownRateChanges,
  scenario: ProjectionScenario = 'BLEND',
  steoByYear: Record<number, number> = steoForecast
): BillPoint[] | undefined => {
  const rates = ratesByState[stateCode]?.rates;
  if (!rates || rates.length < 2 || (!monthlyBillUsd && !monthlyKwh)) {
    return undefined;
  }

  const latest = rates[rates.length - 1];
  const currentYear = latest.year + 1;
  const todayStep = todayStepFraction(stateCode, knownChanges, steoByYear);
  // Entered usage is today's usage too, so it is priced at this year's rate
  const anchorBillUsd = monthlyBillUsd ?? (monthlyKwh ?? 0) * (latest.rate / 100) * (1 + todayStep.fraction);
  const latestEiaBillUsd = anchorBillUsd / (1 + todayStep.fraction);

  const points: BillPoint[] = rates.map(({ rate, year }, i) => ({
    isProjected: false,
    monthlyBillUsd: latestEiaBillUsd * (rate / latest.rate),
    year,
    yoyFraction: i === 0 ? null : rate / rates[i - 1].rate - 1,
  }));

  points.push({
    isProjected: false,
    ...(todayStep.known
      ? { knownChange: { note: todayStep.known.note, sourceUrl: todayStep.known.sourceUrl } }
      : { forecast: todayStep.steo ? ('STEO' as const) : ('AEO' as const) }),
    monthlyBillUsd: anchorBillUsd,
    year: currentYear,
    yoyFraction: todayStep.fraction,
  });

  const escalator = scenarioEscalator(stateCode, scenario);
  const stateKnownChanges = knownChanges[stateCode] ?? [];

  for (let i = 1; i <= PROJECTION_YEARS; i += 1) {
    const year = currentYear + i;
    const known = stateKnownChanges.find((change) => change.year === year);
    const steo = steoByYear[year];
    const yoyFraction = known ? known.impact : (steo ?? escalator);
    const previous = points[points.length - 1];

    points.push({
      isProjected: true,
      ...(known
        ? { knownChange: { note: known.note, sourceUrl: known.sourceUrl } }
        : { forecast: steo !== undefined ? ('STEO' as const) : scenario }),
      monthlyBillUsd: previous.monthlyBillUsd * (1 + yoyFraction),
      year,
      yoyFraction,
    });
  }

  return points;
};
